import { Alert, Box, Button, Chip, TextField, Typography, useTheme } from '@mui/material';
import { JSX, KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseApiError } from '../../../library/apiErrorHelper';
import { HOSTNAME_MAX, IClinicDomain, isUsableHostname } from '../../../library/models/clinic';
import ResourcesIds from '../../../resources/ResourcesIds';
import { ClinicDomainVariables, AddClinicDomainVariables } from '../../../services/clinicsService';
import { useReactMutation } from '../../../services/queryService';
import { services } from '../../../services/services';
import { addButtonStyles, addRowStyles, alertStyles, hintStyles, hostnameStyles, listStyles, rowStyles, sectionStyles, sectionTitleStyles } from './clinicDomains.styles';

interface ClinicDomainsProps {
    clinicId: number;
    /** The clinic's hostnames as the registry last saw them. Seeds this panel, once. */
    domains: IClinicDomain[];
    /** The clinic's new list after any change, so the row behind the dialog can redraw. */
    onChanged: (domains: IClinicDomain[]) => void;
}

/**
 * The hostnames an existing clinic answers on.
 *
 * <p>Every action here takes effect the moment it is asked for, rather than waiting for the
 * dialog's Save. That is not a shortcut -- these are not draft state. Adding a hostname
 * evicts the host resolver's cache so the name starts answering at once, and adding or
 * promoting a primary clears the flag on whichever row held it. Collecting them into a diff
 * to replay on Save would mean a half-applied batch on the first refusal, and a form that
 * claims a hostname is registered before anything has claimed it.
 *
 * <p>Held state rather than props: MUI unmounts a closed dialog's children, so this is seeded
 * afresh every time the dialog opens, and each response replaces it wholesale. The endpoints
 * answer with the clinic's whole list for exactly that -- a response describing only the row
 * that changed could not express the primary moving off another one.
 *
 * <p>It renders inside the clinic form's `<form>`, so every button is `type="button"` and the
 * add field swallows Enter. Either would otherwise submit the clinic form instead.
 */
export const ClinicDomains = (props: ClinicDomainsProps): JSX.Element => {
    const { t: translate } = useTranslation();
    const theme = useTheme();

    const [domains, setDomains] = useState<IClinicDomain[]>(props.domains);
    const [hostname, setHostname] = useState('');
    /** The server's own wording, which is English and cannot be translated. */
    const [error, setError] = useState<string | undefined>(undefined);

    const addDomain = useReactMutation<IClinicDomain[], Error, AddClinicDomainVariables>(
        services.clinics.addClinicDomainMutation()
    );
    const makePrimary = useReactMutation<IClinicDomain[], Error, ClinicDomainVariables>(
        services.clinics.makeClinicDomainPrimaryMutation()
    );
    const removeDomain = useReactMutation<IClinicDomain[], Error, ClinicDomainVariables>(
        services.clinics.removeClinicDomainMutation()
    );

    const isBusy = addDomain.isPending || makePrimary.isPending || removeDomain.isPending;
    const canAdd = isUsableHostname(hostname) && hostname.trim().length <= HOSTNAME_MAX;

    /**
     * One place for the three outcomes, so a failed action cannot leave a stale list behind.
     * Answers whether it worked -- reading `error` back would give this render's value, not
     * the one just set.
     */
    const apply = async (action: Promise<IClinicDomain[]>): Promise<boolean> => {
        setError(undefined);

        try {
            const updated = await action;

            setDomains(updated);
            props.onChanged(updated);

            return true;
        } catch (failure: unknown) {
            setError(parseApiError(failure).message ?? translate(ResourcesIds.CLINIC_DOMAIN_ERROR));

            return false;
        }
    };

    const submitAdd = async (): Promise<void> => {
        if (!canAdd || isBusy) {
            return;
        }

        const added = await apply(addDomain.mutateAsync({
            clinicId: props.clinicId,
            // Never as primary: a clinic's emailed links are not something to re-point as a
            // side effect of registering a name. The first domain becomes primary anyway,
            // which the API decides -- see TenantDomainService.add.
            payload: { hostname: hostname.trim(), primary: false },
        }));

        // Left in the field on failure, so a rejected hostname can be corrected rather than
        // retyped.
        if (added) {
            setHostname('');
        }
    };

    /** Enter in the add field adds; without this it would submit the clinic form. */
    const onAddKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (event.key === 'Enter') {
            event.preventDefault();
            void submitAdd();
        }
    };

    return (
        <Box sx={sectionStyles}>
            <Typography variant="subtitle2" sx={sectionTitleStyles}>
                {translate(ResourcesIds.CLINIC_DOMAINS_TITLE)}
            </Typography>

            <Typography variant="body2" sx={hintStyles}>
                {translate(ResourcesIds.CLINIC_DOMAINS_HINT)}
            </Typography>

            {error && <Alert severity="error" sx={alertStyles}>{error}</Alert>}

            {domains.length === 0 ? (
                <Typography variant="body2" sx={hintStyles}>
                    {translate(ResourcesIds.CLINIC_DOMAINS_EMPTY)}
                </Typography>
            ) : (
                <Box sx={listStyles}>
                    {domains.map(domain => (
                        <Box key={domain.id} sx={rowStyles(theme)}>
                            <Typography variant="body2" sx={hostnameStyles}>
                                {domain.hostname}
                            </Typography>

                            {domain.primary ? (
                                <Chip
                                    size="small"
                                    color="primary"
                                    label={translate(ResourcesIds.CLINIC_DOMAIN_PRIMARY_CHIP)}
                                />
                            ) : (
                                <Button
                                    type="button"
                                    size="small"
                                    disabled={isBusy}
                                    onClick={() => void apply(makePrimary.mutateAsync({
                                        clinicId: props.clinicId, domainId: domain.id,
                                    }))}
                                >
                                    {translate(ResourcesIds.CLINIC_DOMAIN_MAKE_PRIMARY_ACTION)}
                                </Button>
                            )}

                            <Button
                                type="button"
                                size="small"
                                color="error"
                                disabled={isBusy}
                                onClick={() => void apply(removeDomain.mutateAsync({
                                    clinicId: props.clinicId, domainId: domain.id,
                                }))}
                            >
                                {translate(ResourcesIds.CLINIC_DOMAIN_REMOVE_ACTION)}
                            </Button>
                        </Box>
                    ))}
                </Box>
            )}

            <Box sx={addRowStyles}>
                <TextField
                    fullWidth
                    size="small"
                    value={hostname}
                    onChange={event => setHostname(event.target.value)}
                    onKeyDown={onAddKeyDown}
                    label={translate(ResourcesIds.CLINIC_DOMAIN_LABEL)}
                    error={hostname.trim().length > 0 && !canAdd}
                    helperText={hostname.trim().length > 0 && !canAdd
                        ? translate(ResourcesIds.CLINIC_DOMAIN_INVALID)
                        : ' '}
                />

                <Button
                    type="button"
                    variant="outlined"
                    disabled={!canAdd || isBusy}
                    onClick={() => void submitAdd()}
                    sx={addButtonStyles}
                >
                    {translate(ResourcesIds.CLINIC_DOMAIN_ADD_ACTION)}
                </Button>
            </Box>
        </Box>
    );
};
